import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "./useTenant";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import {
  salesRepository,
  saleEventsRepository,
  checkoutSessionsRepository,
} from "@/services";
import { getExpirationMinutes } from "@/utils/checkoutExpiration";

type SalesChannel = Database["public"]["Enums"]["sales_channel"];
type DeliveryMethod = Database["public"]["Enums"]["delivery_method"];
type PaymentMethod = Database["public"]["Enums"]["payment_method"];
type PaymentStatus = Database["public"]["Enums"]["payment_status"];

export interface SaleItem {
  id?: string;
  product_id: string | null;
  variation_id: string | null;
  product_name: string;
  product_code?: string | null;
  variation_description: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface ShippingAddress {
  cep: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface ShippingService {
  name: string;
  company: string;
  price: number;
  id?: string | number;
}

export interface PickupStore {
  id: string;
  name: string;
  street: string | null;
  number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  cep: string | null;
}

export interface CheckoutCustomerData {
  name?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  instagram?: string | null;
  instagram_username?: string | null;
  document?: string | null;
  cep?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface CheckoutPayment {
  id: string;
  status: string;
  payment_method?: string | null;
  paid_at?: string | null;
  created_at?: string;
  metadata?: Record<string, unknown> | null;
}

export interface CheckoutSession {
  id: string;
  status: string;
  expires_at: string;
  created_at?: string;
  updated_at?: string;
  customer_id?: string | null;
  delivery_method?: DeliveryMethod | null;
  metadata?: Record<string, unknown> | null;
  payment_method?: string | null;
  customer_data?: CheckoutCustomerData | Record<string, unknown> | null;
  shipping_address?: ShippingAddress | Record<string, unknown> | null;
  shipping_cost?: number | null;
  shipping_service?: ShippingService | Record<string, unknown> | null;
  pickup_address_id?: string | null;
  pickup_store?: PickupStore | null;
  items?: Array<{ product_id?: string; variation_id?: string | null; variation_description?: string | null; name?: string; unit_price?: number; quantity?: number }> | null;
  checkout_payments?: CheckoutPayment[];
}

export type FulfillmentStatus = "pending" | "ready" | "shipped" | "delivered" | null;

export const getLatestCheckoutSession = (sessions?: CheckoutSession[] | null) => {
  if (!sessions?.length) return null;

  return [...sessions].sort((a, b) => {
    const aHasApprovedPayment = a.checkout_payments?.some((payment) => payment.status === "approved") ? 1 : 0;
    const bHasApprovedPayment = b.checkout_payments?.some((payment) => payment.status === "approved") ? 1 : 0;
    if (aHasApprovedPayment !== bHasApprovedPayment) return bHasApprovedPayment - aHasApprovedPayment;

    const aIsCompleted = a.status === "completed" ? 1 : 0;
    const bIsCompleted = b.status === "completed" ? 1 : 0;
    if (aIsCompleted !== bIsCompleted) return bIsCompleted - aIsCompleted;

    const aHasDeliveryInfo = (a.shipping_address ? 1 : 0) || (a.pickup_address_id ? 1 : 0);
    const bHasDeliveryInfo = (b.shipping_address ? 1 : 0) || (b.pickup_address_id ? 1 : 0);
    if (aHasDeliveryInfo !== bHasDeliveryInfo) return bHasDeliveryInfo - aHasDeliveryInfo;

    const aTime = new Date(a.updated_at || a.created_at || a.expires_at).getTime();
    const bTime = new Date(b.updated_at || b.created_at || b.expires_at).getTime();
    return bTime - aTime;
  })[0];
};

export interface Sale {
  id: string;
  tenant_id: string;
  customer_id: string | null;
  sales_channel: SalesChannel | null;
  delivery_method: DeliveryMethod | null;
  payment_method: PaymentMethod | null;
  payment_status: PaymentStatus | null;
  fulfillment_status: FulfillmentStatus;
  subtotal: number;
  discount: number | null;
  total: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  stock_deducted?: boolean;
  shipping_status?: string | null;
  queue_origin?: boolean;
  // TODO: remove cast post-deploy types regen
  bag_id?: string | null;
  customer?: {
    id: string;
    name: string | null;
    email: string | null;
    whatsapp: string | null;
    instagram: string | null;
    document: string | null;
    cep: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    neighborhood: string | null;
    city: string | null;
    state: string | null;
  } | null;
  sale_items?: SaleItem[];
  checkout_sessions?: CheckoutSession[];
}

export interface SaleFormData {
  customer_id: string | null;
  sales_channel: SalesChannel | null;
  delivery_method: DeliveryMethod | null;
  payment_method: PaymentMethod | null;
  payment_status: PaymentStatus | null;
  discount: number | null;
  notes: string | null;
  items: SaleItem[];
}

export function useSales() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const tenantId = tenant?.id;

  const insertSaleEvent = async (params: {
    saleId: string;
    eventType: string;
    message: string;
  }) => {
    if (!tenantId) return;
    const { saleId, eventType, message } = params;
    await saleEventsRepository.create({
      tenant_id: tenantId,
      sale_id: saleId,
      event_type: eventType,
      message,
    });
  };

  const invalidateProductsCache = () => {
    queryClient.invalidateQueries({ queryKey: ["products", tenantId] });
    queryClient.invalidateQueries({ queryKey: ["products-paginated"] });
  };

  const createSale = useMutation({
    mutationFn: async (data: SaleFormData) => {
      if (!tenantId) throw new Error("Tenant não encontrado");

      const subtotal = data.items.reduce((sum, item) => sum + item.total_price, 0);
      const discount = data.discount || 0;
      const total = subtotal - discount;

      const { data: saleId, error: rpcError } = await supabase.rpc("atomic_create_sale", {
        p_tenant_id: tenantId,
        p_sale_data: {
          customer_id: data.customer_id,
          sales_channel: data.sales_channel,
          delivery_method: data.delivery_method,
          payment_method: data.payment_method,
          payment_status: data.payment_status || "pending",
          subtotal,
          discount,
          total,
          notes: data.notes,
        },
        p_items: data.items.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_code: item.product_code || "",
          variation_id: item.variation_id || "",
          variation_description: item.variation_description || "",
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        })),
      });

      if (rpcError) throw rpcError;

      return { id: saleId } as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["customer-sales"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["bags"] });
      queryClient.invalidateQueries({ queryKey: ["open-bag"] });
      invalidateProductsCache();
      toast.success("Venda registrada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao registrar venda");
    },
  });

  const updateSale = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SaleFormData }) => {
      const { data: saleRow } = await supabase
        .from("sales")
        .select("sales_channel, payment_status")
        .eq("id", id)
        .eq("tenant_id", tenantId)
        .single();

      const currentPaymentStatus = saleRow?.payment_status;

      if (data.payment_status === "cancelled" && currentPaymentStatus !== "cancelled") {
        const { error: cancelError } = await supabase.rpc("atomic_cancel_sale", {
          p_sale_id: id,
          p_tenant_id: tenantId,
          p_event_message: "Cancelado pela edição da venda",
        });
        if (cancelError) throw cancelError;
        return;
      }

      const { data: existingSession } = await checkoutSessionsRepository.findBySaleId(tenantId, id);
      const shippingCost = Number(existingSession?.shipping_cost || 0);

      const subtotal = data.items.reduce((sum, item) => sum + item.total_price, 0);
      const discount = data.discount || 0;
      const total = Math.max(0, subtotal - discount) + shippingCost;

      const { error: rpcError } = await supabase.rpc("atomic_update_sale_items", {
        p_sale_id: id,
        p_tenant_id: tenantId,
        p_sale_data: {
          customer_id: data.customer_id,
          sales_channel: data.sales_channel,
          delivery_method: data.delivery_method,
          payment_method: data.payment_method,
          payment_status: data.payment_status,
          subtotal,
          discount,
          total,
          notes: data.notes,
        },
        p_items: data.items.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_code: item.product_code || "",
          variation_id: item.variation_id || "",
          variation_description: item.variation_description || "",
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        })),
      });

      if (rpcError) throw rpcError;

      const { data: tenantConfig } = await supabase
        .from('tenants')
        .select('checkout_expiration_config')
        .eq('id', tenantId)
        .single();

      const expMinutes = getExpirationMinutes(tenantConfig?.checkout_expiration_config, saleRow?.sales_channel ?? data.sales_channel);

      if (existingSession) {
        const currentMetadata =
          existingSession.metadata && typeof existingSession.metadata === "object" && !Array.isArray(existingSession.metadata)
            ? existingSession.metadata
            : {};
        const checkoutItems = data.items.map((item) => ({
          product_id: item.product_id,
          name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          variation_id: item.variation_id,
          variation_description: item.variation_description,
        }));

        await checkoutSessionsRepository.update(tenantId, existingSession.id, {
          customer_id: data.customer_id || null,
          items: checkoutItems,
          subtotal: subtotal,
          total: Math.max(0, subtotal - discount) + shippingCost,
          metadata: {
            ...currentMetadata,
            sale_discount: discount,
            original_subtotal: subtotal,
          },
          expires_at: new Date(Date.now() + expMinutes * 60 * 1000).toISOString(),
        });
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.removeQueries({ queryKey: ["sale-detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["sales-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["customer-sales"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenantId] });
      invalidateProductsCache();
      toast.success("Venda atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao atualizar venda");
    },
  });

  const deleteSale = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("atomic_cancel_sale", {
        p_sale_id: id,
        p_tenant_id: tenantId,
        p_event_message: "Pedido cancelado pela loja",
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["sale-history"] });
      queryClient.invalidateQueries({ queryKey: ["customer-sales"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenantId] });
      invalidateProductsCache();
      toast.success("Venda cancelada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao cancelar venda");
    },
  });

  const updateFulfillmentStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      deliveryMethod,
    }: {
      id: string;
      status: FulfillmentStatus;
      deliveryMethod: DeliveryMethod | null;
    }) => {
      const { error } = await salesRepository.updateFulfillmentStatus(tenantId, id, status as string);
      if (error) throw error;

      if (status === "ready") {
        await insertSaleEvent({
          saleId: id,
          eventType: "fulfillment_ready",
          message: "Pedido pronto para entrega/retirada",
        });
      }

      if (status === "delivered") {
        if (deliveryMethod === "delivery") {
          await insertSaleEvent({ saleId: id, eventType: "dispatched", message: "Despachado" });
          await insertSaleEvent({ saleId: id, eventType: "concluded", message: "Concluída" });
        } else {
          await insertSaleEvent({ saleId: id, eventType: "delivered", message: "Entregue ao cliente" });
          await insertSaleEvent({ saleId: id, eventType: "concluded", message: "Concluída" });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["sale-history"] });
      toast.success("Status do pedido atualizado!");
    },
    onError: () => {
      toast.error("Erro ao atualizar status do pedido");
    },
  });

  const confirmPickupPayment = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error("Tenant não encontrado");

      const { data, error } = await supabase
        .from('sales')
        .update({
          payment_status: 'paid',
          fulfillment_status: 'delivered',
        })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .eq('payment_status', 'pay_on_pickup')
        .select();

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Venda não está em status pay_on_pickup');
      }

      await insertSaleEvent({
        saleId: id,
        eventType: "payment_confirmed",
        message: "Pagamento na retirada confirmado",
      });
      await insertSaleEvent({
        saleId: id,
        eventType: "concluded",
        message: "Concluída",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["sale-history"] });
      toast.success("Pagamento confirmado! Venda concluída.");
    },
    onError: () => {
      toast.error("Erro ao confirmar pagamento");
    },
  });

  const reactivateExpiredSale = useMutation({
    mutationFn: async (saleId: string) => {
      if (!tenantId) throw new Error("Tenant não encontrado");

      const { data: targetSale, error: fetchError } = await supabase
        .from("sales")
        .select("id, payment_status, sales_channel, checkout_sessions(id, status, expires_at)")
        .eq("id", saleId)
        .eq("tenant_id", tenantId)
        .single();

      if (fetchError || !targetSale) throw new Error("Venda não encontrada");

      const session = (targetSale.checkout_sessions as Array<{ id: string; status: string; expires_at: string }> | null)?.[0];
      const isExpiredBySession = session && new Date(session.expires_at) < new Date() && session.status !== "completed" && session.status !== "cancelled";
      const isExpired = targetSale.payment_status === "expired" || isExpiredBySession;

      if (!isExpired) {
        throw new Error("Apenas vendas expiradas podem ser reativadas");
      }
      if (!session) {
        throw new Error("Sessão de checkout não encontrada");
      }

      const { data: tenantConfig } = await supabase
        .from('tenants')
        .select('checkout_expiration_config')
        .eq('id', tenantId)
        .single();

      const expMinutes = getExpirationMinutes(tenantConfig?.checkout_expiration_config, targetSale.sales_channel);
      const newExpiresAt = new Date(Date.now() + expMinutes * 60 * 1000).toISOString();

      const { data: updatedSale, error: saleError } = await supabase
        .from('sales')
        .update({ payment_status: 'pending' })
        .eq('id', saleId)
        .eq('tenant_id', tenantId)
        .in('payment_status', ['expired', 'pending'])
        .select();

      if (saleError) throw saleError;
      if (!updatedSale || updatedSale.length === 0) {
        throw new Error('Venda não pode ser reativada (status atual não permite reativação)');
      }

      const { error: checkoutError } = await checkoutSessionsRepository.update(
        tenantId,
        session.id,
        {
          status: "pending",
          expires_at: newExpiresAt,
        }
      );
      if (checkoutError) throw checkoutError;

      const { error: clearPaymentsError } = await supabase
        .from("checkout_payments")
        .update({
          status: "cancelled",
          metadata: { reactivated_at: new Date().toISOString(), reason: "sale_reactivated" },
        })
        .eq("checkout_session_id", session.id)
        .in("status", ["pending", "in_process"]);
      if (clearPaymentsError) throw clearPaymentsError;

      await insertSaleEvent({
        saleId,
        eventType: "checkout_reactivated",
        message: "Checkout reativado após expiração",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["sale-history"] });
      toast.success("Venda reativada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao reativar venda expirada");
    },
  });

  return {
    createSale,
    updateSale,
    deleteSale,
    updateFulfillmentStatus,
    confirmPickupPayment,
    reactivateExpiredSale,
  };
}

export function useSaleMutations() {
  const {
    createSale,
    updateSale,
    deleteSale,
    updateFulfillmentStatus,
    confirmPickupPayment,
    reactivateExpiredSale,
  } = useSales();

  return {
    createSale,
    updateSale,
    deleteSale,
    updateFulfillmentStatus,
    confirmPickupPayment,
    reactivateExpiredSale,
  };
}

export function useSaleById(saleId: string | null) {
  const { tenant } = useTenant();
  const tenantId = tenant?.id;

  return useQuery({
    queryKey: ["sale", saleId],
    queryFn: async () => {
      if (!saleId) return null;
      const { data, error } = await salesRepository.getById(tenantId!, saleId);
      if (error) throw error;
      return data as Sale;
    },
    enabled: !!saleId && !!tenantId,
  });
}

export function useCustomerSales(customerId: string | null) {
  const { tenant } = useTenant();
  const tenantId = tenant?.id;

  return useQuery({
    queryKey: ["customer-sales", customerId, tenantId],
    queryFn: async () => {
      if (!customerId || !tenantId) return { sales: [], stats: null };

      const { data: sales, error } = await salesRepository.listByCustomer(tenantId, customerId);
      if (error) throw error;

      const paidSales = sales.filter(sale => sale.payment_status === 'paid');

      const stats = {
        totalPurchases: sales.length,
        totalSpent: paidSales.reduce((sum, sale) => sum + Number(sale.total), 0),
        lastPurchase: sales.length > 0 ? new Date(sales[0].created_at) : null,
      };

      return { sales, stats };
    },
    enabled: !!customerId && !!tenantId,
    staleTime: 1000 * 60 * 5,
  });
}
